'use client';

import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { LogIn } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { setFlashToast } from '@/lib/flash-toast';
import { useAuthSession } from '@/components/rbac/AuthSessionProvider';
import { useAdminPendientes } from '@/components/admin/AdminPendientesProvider';
import { LayoutIcon } from './icons';
import { SidebarMenu } from './SidebarMenu';
import { SidebarHeader } from './SidebarHeader';
import { SidebarFooter } from './SidebarFooter';
import { TutorialHelpButton } from '@/components/tutoriales/TutorialHelpButton';
import { useBreadcrumbLeaf } from './BreadcrumbLeafContext';
import { BREADCRUMB_BADGE_PORTAL_ID } from './breadcrumb-badge';
import { SidePanelAside } from './SidePanelAside';
import { SidePanelProvider } from './SidePanelContext';
import { ADMIN_NAVIGATION_ORDER, FOOTER_NAVIGATION_ORDER } from './navigation';
import udaCompactLogo from './assets/uda-compact-logo.png';
import udaExtendedLogo from './assets/uda-extended-logo.png';
import type { LayoutIconName, NavigationSection } from './types';
import styles from './AppShell.module.css';

interface AppShellProps {
	children: ReactNode;
	navigation: NavigationSection[];
}

interface BreadcrumbItem {
	href: string;
	label: string;
	icon: LayoutIconName;
}

const ROUTE_LABEL_MAP: Record<string, string> = {
	'/': 'Inicio',
	'/publicaciones': 'Publicaciones',
	'/proyectos': 'Proyectos',
	'/investigadores': 'Investigadores',
	'/graficos': 'Gráficos',
	'/lineas': 'Líneas de Investigación',
	'/editor': 'Gestión Editorial',
	'/admin': 'Administración',
	'/revisiones': 'Revisiones',
	'/mi-perfil': 'Mi Perfil',
	'/configuracion': 'Configuración',
	'/admin/alertas': 'Alertas',
	'/admin/auditoria': 'Auditoría',
	'/admin/solicitudes': 'Solicitudes',
	'/admin/automatizaciones': 'Automatizaciones',
	'/admin/publicaciones': 'Publicaciones',
	'/admin/proyectos': 'Proyectos',
	'/admin/lineas': 'Líneas de Investigación',
	'/admin/configuraciones': 'Configuraciones',
	'/admin/parametros': 'Parámetros del sistema',
	'/admin/eventos': 'Eventos',
	'/admin/revalidaciones': 'Revalidaciones',
	'/admin/investigadores': 'Investigadores',
};

const SEGMENT_LABEL_MAP: Record<string, string> = {
	editor: 'Gestión Editorial',
	admin: 'Administración',
	revisiones: 'Revisiones',
	'mi-perfil': 'Mi Perfil',
	configuracion: 'Configuración',
	publicaciones: 'Publicaciones',
	proyectos: 'Proyectos',
	investigadores: 'Investigadores',
	lineas: 'Líneas de Investigación',
	correos: 'Correos',
	nueva: 'Nueva plantilla',
};

const ROUTE_ICON_MAP: Record<string, LayoutIconName> = {
	'/': 'home',
	'/publicaciones': 'file',
	'/proyectos': 'layers',
	'/investigadores': 'users',
	'/lineas': 'graduation-cap',
	'/editor': 'file-check',
	'/admin': 'shield-check',
	'/revisiones': 'clipboard-check',
	'/mi-perfil': 'user-round',
	'/configuracion': 'settings',
};

export function AppShell({ children, navigation }: AppShellProps) {
	const pathname = usePathname();
	const router = useRouter();
	const [desktopExpanded, setDesktopExpanded] = useState(false);
	const [mobileOpen, setMobileOpen] = useState(false);
	const [sidebarHovered, setSidebarHovered] = useState(false);
	const { user, status } = useAuthSession();
	const { resumen } = useAdminPendientes();

	// Sidebar pending-count badge (R50): total across all admin categories,
	// shown on the "Administración" nav item. Zero / non-admin → no badge.
	const navBadges = useMemo(
		() => ({ '/admin': resumen?.total ?? 0 }),
		[resumen],
	);

	const [transitionDir, setTransitionDir] = useState<'up' | 'down' | 'left' | 'right'>('up');
	const prevPathRef = useRef(pathname);

	// ─── Desvanecido de los filos del área que scrollea ──────────────────────
	//
	// `.appShellMain` deja 1.5rem de hueco arriba y abajo, y el contenido se
	// cortaba en seco contra él: se leía como un borde que tapa la página. Las
	// dos franjas de degradado (`::before` / `::after` en el CSS) lo disuelven.
	//
	// Se encienden SOLO cuando hay contenido hacia ese lado. Un degradado fijo
	// abajo del todo se lee como una mancha, y arriba del todo velaría el borde
	// superior de la primera tarjeta.
	//
	// El estado va en `dataset`, no en React: esto corre en cada evento de scroll
	// y un `setState` ahí re-renderizaría el shell entero decenas de veces por
	// gesto. Además el repo prohíbe `setState` dentro de un efecto.
	const mainRef = useRef<HTMLElement | null>(null);
	const scrollerRef = useRef<HTMLDivElement | null>(null);

	// `--siiv-scrollbar` la mide un script síncrono en el `<head>` (ver
	// `app/layout.tsx`): tiene que estar antes del primer pintado, o el primer
	// cuadro sale con 0 y todo salta al hidratar.

	// Depende de `pathname` porque el div que scrollea lleva `key={pathname}`:
	// cada navegación lo REMONTA, y el listener tiene que colgarse del nodo nuevo.
	useEffect(() => {
		const scroller = scrollerRef.current;
		const main = mainRef.current;
		if (!scroller || !main) return;

		// 4px de margen: sin él, el redondeo sub-pixel del scroll hace parpadear
		// la franja de abajo al llegar al final.
		const MARGEN = 4;

		const actualizar = () => {
			main.dataset.fadeTop = String(scroller.scrollTop > MARGEN);
			main.dataset.fadeBottom = String(
				scroller.scrollTop + scroller.clientHeight < scroller.scrollHeight - MARGEN,
			);
		};

		actualizar();
		scroller.addEventListener('scroll', actualizar, { passive: true });

		// El alto del contenido cambia sin que haya scroll ni resize de ventana:
		// una imagen que carga, un acordeón que se abre, la vitrina que rota. Se
		// observa el contenido, no solo el contenedor.
		const observer = new ResizeObserver(actualizar);
		observer.observe(scroller);
		if (scroller.firstElementChild) observer.observe(scroller.firstElementChild);

		return () => {
			scroller.removeEventListener('scroll', actualizar);
			observer.disconnect();
		};
	}, [pathname]);

	// La pila completa de menús, de arriba abajo, tal como se ven: los ítems del
	// sidebar, los módulos de /admin (que cuelgan de su ítem) y el pie. Define la
	// dirección vertical de la transición entre menús.
	const menuOrder = useMemo(
		() => [
			...navigation.flatMap((section) => section.items.map((item) => item.href)),
			...ADMIN_NAVIGATION_ORDER,
			...FOOTER_NAVIGATION_ORDER,
		],
		[navigation],
	);

	useEffect(() => {
		setMobileOpen(false);

		if (prevPathRef.current !== pathname) {
			const oldIndex = resolveMenuIndex(prevPathRef.current, menuOrder);
			const newIndex = resolveMenuIndex(pathname, menuOrder);

			if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
				// Cambio de menú a menú: vertical, siguiendo el orden del menú, como si
				// las páginas estuvieran apiladas y la vista se moviera por la pila.
				// Bajar en el menú trae el contenido desde abajo; subir, desde arriba.
				setTransitionDir(newIndex > oldIndex ? 'up' : 'down');
			} else {
				// Misma entrada de menú: es profundidad dentro de la sección (listado ↔
				// detalle). Ese gesto se mantiene horizontal para distinguirlo del salto
				// entre menús.
				const oldDepth = prevPathRef.current.split('/').filter(Boolean).length;
				const newDepth = pathname.split('/').filter(Boolean).length;

				if (newDepth > oldDepth) {
					setTransitionDir('left');
				} else if (newDepth < oldDepth) {
					setTransitionDir('right');
				} else {
					setTransitionDir('up');
				}
			}

			prevPathRef.current = pathname;
		}
	}, [pathname, menuOrder]);

	const leafLabel = useBreadcrumbLeaf();
	const breadcrumbs = useMemo(() => {
		const crumbs = buildBreadcrumbs(pathname);
		if (leafLabel && crumbs.length > 0) {
			crumbs[crumbs.length - 1] = { ...crumbs[crumbs.length - 1], label: leafLabel };
		}
		return crumbs;
	}, [pathname, leafLabel]);
	const parentHref = useMemo(() => getParentHref(pathname), [pathname]);
	const canNavigateBack = Boolean(parentHref);
	const loginHref = useMemo(
		() => `/login?next=${encodeURIComponent(pathname)}`,
		[pathname],
	);

	function handleBackNavigation() {
		if (!parentHref) return;
		const search = typeof window !== 'undefined' ? window.location.search : '';
		router.push(`${parentHref}${search}`);
	}

	async function handleLogout() {
		try {
			await apiClient('/auth/logout', { method: 'POST' });
		} finally {
			// Queue the toast instead of firing it now: the hard reload below wipes
			// sonner's in-memory queue before the toast can paint. BrandToaster replays
			// it on the fresh page (see flash-toast.ts).
			setFlashToast({
				variant: 'success',
				title: 'Sesión cerrada',
				description: 'Cerraste sesión correctamente. ¡Hasta pronto!',
			});
			// Full page reload (NOT router.push): a CSR navigation keeps the client
			// Router Cache + AuthSessionProvider state, so authenticated content stays
			// cached and renders stale after logout. A hard reload wipes all client
			// state and re-fetches everything as anonymous. Empirically confirmed.
			// (Also avoids the post-logout /auth/me 401 that refreshSession triggered.)
			window.location.href = '/';
		}
	}

	return (
		<SidePanelProvider>
		<div className={styles.appShellRoot}>
			<div className={styles.appShellDesktopLayout}>
				<aside
					className={`${styles.appShellDesktopAside} ${desktopExpanded
						? styles.appShellDesktopAsideExpanded
						: styles.appShellDesktopAsideCollapsed
						}`}
					onMouseEnter={() => setSidebarHovered(true)}
					onMouseLeave={() => setSidebarHovered(false)}
					data-tutorial="home-sidebar"
				>
					<div className={styles.appShellSidebar}>
						<div className={styles.appShellSidebarTop}>
							<div
								className={`${styles.appShellSidebarHeaderPad} ${desktopExpanded
										? styles.appShellSidebarHeaderPadExpanded
										: styles.appShellSidebarHeaderPadCollapsed
									}`}
							>
								<SidebarHeader
									expanded={desktopExpanded}
									sidebarHovered={sidebarHovered}
									compactLogo={udaCompactLogo}
									extendedLogo={udaExtendedLogo}
									onToggleExpand={() =>
										setDesktopExpanded((prev) => !prev)
									}
								/>
							</div>
							<SidebarMenu
								sections={navigation}
								compact={!desktopExpanded}
								badges={navBadges}
							/>
						</div>

						<div
							className={`${styles.appShellSidebarFooterPad} ${
								desktopExpanded
									? styles.appShellSidebarFooterPadExpanded
									: styles.appShellSidebarFooterPadCollapsed
							}`}
						>
							<SidebarFooter
								expanded={desktopExpanded}
								user={user}
								status={status}
								loginHref={loginHref}
								onLogout={handleLogout}
							/>
						</div>
					</div>
				</aside>

				<div className={styles.appShellMainColumn}>
					<header className={styles.appShellTopbar}>
						<div className={styles.appShellTopbarInner}>
							<div className={styles.appShellTopbarLeft}>
								<button
									type="button"
									onClick={() => setMobileOpen((prev) => !prev)}
									className={styles.appShellMobileToggle}
									aria-label={
										mobileOpen
											? 'Cerrar menú lateral'
											: 'Abrir menú lateral'
									}
								>
									<LayoutIcon
										name={mobileOpen ? 'x' : 'menu'}
										className={styles.appShellMobileToggleIcon}
									/>
								</button>
								{breadcrumbs.length > 0 && (
									<nav
										className={styles.appShellBreadcrumb}
										aria-label="Navegación de página"
									>
										{/* En el inicio no hay atrás: el botón no se muestra (no queda deshabilitado). */}
										{canNavigateBack && (
											<button
												type="button"
												onClick={handleBackNavigation}
												className={styles.appShellBreadcrumbBackButton}
												aria-label="Volver al nivel anterior"
												title="Volver al nivel anterior"
											>
												<LayoutIcon
													name="arrow-left"
													className={styles.appShellBreadcrumbBackButtonIcon}
												/>
											</button>
										)}
										<ol className={styles.appShellBreadcrumbList}>
											{breadcrumbs.map((breadcrumb, index) => {
												const isCurrent =
													index === breadcrumbs.length - 1;
												return (
													<li
														key={breadcrumb.href}
														className={styles.appShellBreadcrumbItem}
													>
														{isCurrent ? (
															<span
																className={
																	styles.appShellBreadcrumbCurrent
																}
																aria-current="page"
															>
																<LayoutIcon
																	name={breadcrumb.icon}
																	className={
																		styles.appShellBreadcrumbIcon
																	}
																/>
																<span className={styles.appShellBreadcrumbLabel}>
																	{breadcrumb.label}
																</span>
																{/* Hueco para que la vista actual cuelgue un dato junto a su
																    título — hoy, cuántos investigadores hay. Va SÓLO en el
																    eslabón actual: al entrar a una ficha, "Investigadores"
																    pasa a ser un enlace del camino y el número no tendría
																    sentido ahí. Se vacía solo al desmontarse la vista.
																    `:empty` lo colapsa para que no deje un hueco muerto. */}
																<span
																	id={BREADCRUMB_BADGE_PORTAL_ID}
																	className={styles.appShellBreadcrumbBadge}
																/>
															</span>
														) : (
															<Link
																href={breadcrumb.href}
																className={
																	styles.appShellBreadcrumbLink
																}
															>
																<LayoutIcon
																	name={breadcrumb.icon}
																	className={
																		styles.appShellBreadcrumbIcon
																	}
																/>
																<span className={styles.appShellBreadcrumbLabel}>
																	{breadcrumb.label}
																</span>
															</Link>
														)}
														{!isCurrent && (
															<LayoutIcon
																name="chevron-right"
																className={
																	styles.appShellBreadcrumbSeparator
																}
															/>
														)}
													</li>
												);
											})}
										</ol>
									</nav>
								)}
							</div>

							<div className={styles.appShellTopbarRight}>
								<TutorialHelpButton />
								{/* Mientras la sesión se resuelve no se sabe qué botón va acá —
								    "Iniciar sesión" o las acciones de la vista— así que se
								    reserva su lugar con una silueta. Sin ella el topbar arranca
								    vacío por ese lado y lo que aparezca después empuja el resto
								    de la fila: un salto en la primera media pantalla que se ve. */}
								{status === 'loading' ? (
									<span
										className={styles.appShellTopbarSkeleton}
										aria-hidden="true"
									/>
								) : null}
								{status !== 'loading' && !user ? (
									<Link
										href={loginHref}
										className={styles.appShellLoginButton}
										aria-label="Iniciar sesión"
									>
										<LogIn size={13} />
										Iniciar sesión
									</Link>
								) : null}
								<div
									id="topbar-actions-portal"
									className={styles.appShellTopbarActions}
								>
									{/* Portal target for page-specific actions */}
								</div>
							</div>
						</div>
					</header>

					{mobileOpen && (
						<div className={styles.appShellMobilePanel}>
							<div className={styles.appShellMobilePanelInner}>
								<SidebarMenu
									sections={navigation}
									badges={navBadges}
									onNavigate={() => setMobileOpen(false)}
								/>
							</div>
							{/* Footer también en móvil. Sin esto no había forma de cerrar
							    sesión en pantallas angostas: el aside de escritorio (que
							    monta el SidebarFooter) está display:none bajo 1024px. */}
							<div className={styles.appShellMobileFooter}>
								<SidebarFooter
									expanded
									user={user}
									status={status}
									loginHref={loginHref}
									onLogout={handleLogout}
									tutorialAnchor={false}
								/>
							</div>
						</div>
					)}

					<main className={styles.appShellMain} ref={mainRef}>
						<div
							key={pathname}
							ref={scrollerRef}
							className={`siiv-scroll siiv-page-transition-${transitionDir} ${styles.appShellPageWrapper}`}
						>
							<div className={styles.appShellContentMaxWidth}>
								{children}
							</div>
						</div>
					</main>
				</div>
				{/* Carril derecho, hermano de la columna principal y del aside izquierdo.
				    Que quede a ese nivel es lo que alinea el topbar con el contenido en
				    las vistas CON panel: la barra superior termina donde termina el área
				    de trabajo, no en el filo de la ventana. Donde no hay panel el carril
				    mide 0 y ninguna vista se entera. */}
				<SidePanelAside />
			</div>
		</div>
		</SidePanelProvider>
	);
}

/**
 * Ubica una ruta en la pila de menús. Gana el href más largo que la contiene: así
 * `/admin/toasts` se resuelve a su propio módulo y no a `/admin`, que también la
 * prefija. Devuelve -1 si la ruta no cuelga de ningún menú.
 */
function resolveMenuIndex(pathname: string, menuOrder: string[]): number {
	let bestIndex = -1;
	let bestLength = -1;

	menuOrder.forEach((href, index) => {
		// El inicio prefija todas las rutas: solo cuenta como match exacto.
		const matches =
			href === '/' ? pathname === '/' : pathname === href || pathname.startsWith(`${href}/`);
		if (matches && href.length > bestLength) {
			bestIndex = index;
			bestLength = href.length;
		}
	});

	return bestIndex;
}

function buildBreadcrumbs(pathname: string): BreadcrumbItem[] {
	const breadcrumbs: BreadcrumbItem[] = [
		{
			href: '/',
			label: ROUTE_LABEL_MAP['/'] ?? 'Inicio',
			icon: ROUTE_ICON_MAP['/'] ?? 'home',
		},
	];
	const segments = pathname.split('/').filter(Boolean);
	if (!segments.length) {
		return breadcrumbs;
	}

	const nestedBreadcrumbs: BreadcrumbItem[] = [];

	let currentPath = '';
	for (const [index, segment] of segments.entries()) {
		currentPath = `${currentPath}/${segment}`;
		const basePath = `/${segments[0]}`;
		const label =
			ROUTE_LABEL_MAP[currentPath] ??
			SEGMENT_LABEL_MAP[segment] ??
			formatSegmentLabel(segment, segments, index);
		const icon =
			ROUTE_ICON_MAP[currentPath] ??
			(segment === 'exportar'
				? 'printer'
				: segment === 'editar'
					? 'pencil'
					: index === 0
						? ROUTE_ICON_MAP[basePath] ?? 'file'
						: resolveNestedIcon(basePath));

		nestedBreadcrumbs.push({
			href: currentPath,
			label,
			icon,
		});
	}

	return nestedBreadcrumbs;
}

function formatSegmentLabel(segment: string, segments: string[], index: number) {
	const decodedSegment = decodeURIComponent(segment).trim();
	const normalized = stripInvestigatorUidSuffix(decodedSegment, segments, index)
		.replace(/[-_]+/g, ' ')
		.trim();
	if (!normalized) return 'Detalle';
	return normalized
		.split(/\s+/)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
}

function stripInvestigatorUidSuffix(segment: string, segments: string[], index: number) {
	const isInvestigatorSlugSegment = segments[0] === 'investigadores' && index === 1;
	if (!isInvestigatorSlugSegment) return segment;

	const match = segment.match(/^(.*)-([a-z0-9]{6,})$/i);
	if (!match?.[1]) return segment;

	return match[1];
}

function resolveNestedIcon(basePath: string): LayoutIconName {
	if (basePath === '/investigadores') return 'user-round';
	if (basePath === '/publicaciones') return 'file';
	if (basePath === '/proyectos') return 'layers';
	return ROUTE_ICON_MAP[basePath] ?? 'file';
}

/**
 * A dónde lleva el botón "atrás". Un menú de primer nivel (`/investigadores`,
 * `/admin`…) vuelve al inicio: es el nivel más alto, no hay padre propio. Solo el
 * inicio devuelve null —no tiene atrás— y ahí el botón no se renderiza.
 * Es puramente funcional: NO altera el breadcrumb que se muestra.
 */
function getParentHref(pathname: string) {
	const segments = pathname.split('/').filter(Boolean);
	if (!segments.length) return null;
	if (segments.length === 1) return '/';
	return `/${segments.slice(0, -1).join('/')}`;
}

function normalizeRole(role: string) {
	if (role === 'admin') return 'Admin';
	if (role === 'editor') return 'Editor';
	if (role === 'investigador') return 'Investigador';
	return role;
}
